import { LocalNotifications } from '@capacitor/local-notifications';

import { Language, translateIn } from '../i18n';
import { isNative } from '../monetization/config';

import { dailyFor, TarotState } from './tarotSlice';

/** Local hour of the morning nudge for a card that has not been drawn yet. */
export const MORNING_HOUR = 9;

/** How many mornings ahead are scheduled on the device; every launch and every draw re-plans them. */
export const MORNING_DAYS = 7;

export type ReminderKind = 'morning' | 'midnight';

export interface Reminder {
    id: number;
    kind: ReminderKind;
    at: Date;
}

const MIDNIGHT_ID = 1;
const MORNING_ID_BASE = 100;

/**
 * What to remind the person of, from `now` on:
 * - a morning nudge for each of the next days, except today's when the card is already drawn;
 * - one at the coming midnight, only if today's card was drawn (a new reading becomes available then).
 * Opening the app re-plans, so drawing tomorrow's card before the nudge cancels it.
 */
export const planReminders = (
    state: TarotState,
    now: Date = new Date()
): Reminder[] => {
    if (!state.reminders) return [];
    const drawnToday = Boolean(dailyFor(state, now));
    const plan: Reminder[] = [];

    if (drawnToday) {
        plan.push({
            id: MIDNIGHT_ID,
            kind: 'midnight',
            at: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1),
        });
    }
    for (let d = 0; d < MORNING_DAYS; d++) {
        if (d === 0 && drawnToday) continue;
        const at = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() + d,
            MORNING_HOUR
        );
        if (at.getTime() > now.getTime())
            plan.push({ id: MORNING_ID_BASE + d, kind: 'morning', at });
    }
    return plan.sort((a, b) => a.at.getTime() - b.at.getTime());
};

const textFor = (kind: ReminderKind, language: Language) => ({
    title: translateIn(language, `reminders.${kind}.title`),
    body: translateIn(language, `reminders.${kind}.body`),
});

/** Whether this device can show reminders at all (the Android app, or a browser with notifications). */
export const remindersSupported = (): boolean =>
    isNative() ||
    (typeof Notification !== 'undefined' && 'serviceWorker' in navigator);

/** Asks the person for permission. True only when reminders can actually be shown. */
export const requestReminderPermission = async (): Promise<boolean> => {
    try {
        if (isNative()) {
            const { display } = await LocalNotifications.requestPermissions();
            return display === 'granted';
        }
        if (typeof Notification === 'undefined') return false;
        return (await Notification.requestPermission()) === 'granted';
    } catch {
        return false;
    }
};

let webTimer: number | undefined;

const showOnWeb = async (kind: ReminderKind, language: Language) => {
    const { title, body } = textFor(kind, language);
    const options = {
        body,
        icon: '/apple-touch-icon.png',
        tag: `arcana-${kind}`,
    };
    try {
        // `ready` never resolves where no worker is registered (the dev server), so ask for the registration.
        const registration = await navigator.serviceWorker?.getRegistration();
        if (registration) await registration.showNotification(title, options);
        else new Notification(title, options);
    } catch {
        // Notifications blocked after granting: nothing to do.
    }
};

/**
 * Makes the device match the plan. On Android the notifications are scheduled with the system, so they
 * arrive with the app closed. A browser cannot be woken without a push server, so there the next
 * reminder fires from a timer while the app is open (or kept alive as an installed app).
 */
export const syncReminders = async (
    state: TarotState,
    language: Language,
    now: Date = new Date()
): Promise<void> => {
    const plan = planReminders(state, now);
    if (isNative()) {
        try {
            const pending = await LocalNotifications.getPending();
            if (pending.notifications.length)
                await LocalNotifications.cancel(pending);
            if (!plan.length) return;
            await LocalNotifications.schedule({
                notifications: plan.map((r) => ({
                    id: r.id,
                    ...textFor(r.kind, language),
                    schedule: { at: r.at, allowWhileIdle: true },
                    smallIcon: 'ic_stat_arcana',
                    channelId: 'arcana-reminders',
                })),
            });
        } catch (error) {
            console.warn(
                'Reminders could not be scheduled:',
                (error as Error)?.message
            );
        }
        return;
    }

    window.clearTimeout(webTimer);
    webTimer = undefined;
    if (
        typeof Notification === 'undefined' ||
        Notification.permission !== 'granted'
    )
        return;
    const next = plan[0];
    if (!next) return;
    // setTimeout caps at ~24.8 days; the plan never reaches that far.
    webTimer = window.setTimeout(
        () => showOnWeb(next.kind, language),
        next.at.getTime() - now.getTime()
    );
};

/** The channel Android 8+ requires; low-key so the midnight one does not buzz the night. */
export const ensureReminderChannel = async (
    language: Language
): Promise<void> => {
    if (!isNative()) return;
    try {
        await LocalNotifications.createChannel({
            id: 'arcana-reminders',
            name: translateIn(language, 'reminders.channel'),
            importance: 3,
            vibration: false,
        });
    } catch {
        // The default channel is used instead.
    }
};
